import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createRole, fetchRole, updateRole } from '../../../api/RolesAPI';
import { getPermissions } from '../../../api/PermissionsAPI';
import { handleApiError } from '../../../api/risapi';
import { capitalizeFirstLetter } from '../../../commonfunctions';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';

const RolesForm = ({ formType }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [role, setRole] = useState({ name: '' });
    const [permissions, setPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    const isEditMode = formType === 'edit';

    // Fetch all available permissions on component mount
    useEffect(() => {
        getPermissions({ paginate: false }, (response) => {
            if (response.permissions) setPermissions(response.permissions.data || response.permissions);
        });
    }, []);

    // Fetch role data if in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            setIsLoading(true);
            fetchRole(id, (response) => {
                setIsLoading(false);
                if (response.role) {
                    setRole(response.role);
                    setSelectedPermissions(new Set(response.role.permission_ids));
                }
            });
        }
    }, [id, isEditMode]);

    const handleInputChange = (e) => setRole({ ...role, name: e.target.value });

    const handlePermissionChange = (permissionId, isChecked) => {
        setSelectedPermissions(prev => {
            const newSelected = new Set(prev);
            if (isChecked) newSelected.add(permissionId);
            else newSelected.delete(permissionId);
            return newSelected;
        });
    };

    const handleSubmit = (exitForm) => {
        setIsLoading(true);
        setErrors({});
        const dataToSend = { ...role, permission_ids: Array.from(selectedPermissions) };
        const apiCall = isEditMode ? updateRole : createRole;

        apiCall(dataToSend, (response) => {
            setIsLoading(false);
            if (response.role) {
                const message = `Role ${isEditMode ? 'updated' : 'created'} successfully!`;
                if (exitForm) {
                    navigate('/club_portal/roles', { state: { message } });
                } else {
                    setToaster({ type: 'success', heading: 'Success', message });
                    setShowToaster(true);
                    setRole({ name: '' });
                    setSelectedPermissions(new Set());
                }
            } else {
                handleApiError(response, setErrors);
            }
        });
    };

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title={isEditMode ? 'Edit Role' : 'Create Role'}>
                    <li><a className="f-s-14 f-w-500" href="#">Access Control</a></li>
                    <li><a className="f-s-14 f-w-500" href="/club_portal/roles">Roles</a></li>
                    <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                </AppBreadCrumb>
                <div className="card">
                     <div className="card-header"><h5>{isEditMode ? `Editing: ${role.name}` : 'Role Details'}</h5></div>
                    <div className="card-body">
                        <form className="app-form">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label className="form-label">Role Name *</label>
                                    <input className="form-control" placeholder="e.g., captain" value={role.name} onChange={handleInputChange} />
                                    {errors.name && <div className="text-danger small">{errors.name}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label">Assign Permissions</label>
                                    <div className="permission-grid">
                                        {permissions.map(p => (
                                            <div key={p.id} className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`perm-${p.id}`}
                                                    checked={selectedPermissions.has(p.id)}
                                                    onChange={(e) => handlePermissionChange(p.id, e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor={`perm-${p.id}`}>{p.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {errors.permission_ids && <div className="text-danger small">{errors.permission_ids}</div>}
                                </div>
                                <div className="col-12 text-end">
                                    {!isEditMode ? (
                                        <>
                                            <button type="button" className="btn btn-outline-primary me-2 btn-sm" onClick={() => handleSubmit(false)}>Save + Next</button>
                                            <button type="button" className="btn btn-primary me-2 btn-sm" onClick={() => handleSubmit(true)}>Save + Exit</button>
                                        </>
                                    ) : (
                                        <>
                                            <button type="button" className="btn btn-primary me-2 btn-sm" onClick={() => handleSubmit(true)} disabled={isLoading}>
                                                {isLoading ? 'Updating...' : 'Update Role'}
                                            </button>
                                            <button type="button" className="btn btn-danger btn-sm" onClick={() => navigate('/club_portal/roles')}>Cancel</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <style>{`.permission-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; }`}</style>
        </main>
    );
};

export default RolesForm;

